<script setup lang="ts">
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui';
import { computed, nextTick, ref, toRef, useAttrs, type VNode, watchEffect } from 'vue';

import { KaButton } from '@/kasten-components/components/KaButton';
import { useDataTestid, useDialogTriggerFocus } from '@/kasten-components/composables';
import type { VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';

import {
  modalButtonsGridClasses,
  modalConfirmBodyClasses,
  modalContainerClasses,
  modalContentClasses,
  modalContentTransition,
  modalDescriptionClasses,
  modalOverlayClasses,
  modalOverlayTransition,
  modalTitleClasses,
} from './modal-variants';

defineOptions({
  inheritAttrs: false,
});

export interface KaModalProps {
  open: boolean;
  preset?: 'confirm';
  title?: string;
  description?: string;
  confirmText?: string;
  dismissText?: string;
  zIndexClass?: VueClass;
  width?: 'full' | 'auto';
  modalClasses?: VueClass;
  isPending?: boolean;
  submitBtnFormAttr?: string;
  initialFocus?: HTMLElement | null;
  initiallyFocusOnConfirm?: boolean;
  initiallyFocusOnCancel?: boolean;
  overlayClasses?: VueClass;
  modal?: boolean;
  /**
   * Element to return focus to when the dialog closes. Pass this when the dialog
   * is opened from a transient trigger (a menu item, popover, etc.) that unmounts
   * before the dialog closes — otherwise reka's default restore target is gone and
   * focus falls through to `<body>`. When omitted, the element focused at open time
   * is captured as a fallback. See K10-36133.
   */
  triggerEl?: HTMLElement | null;
}

const props = withDefaults(defineProps<KaModalProps>(), {
  title: undefined,
  description: '',
  confirmText: 'Confirm',
  dismissText: 'Cancel',
  zIndexClass: 'z-50',
  initiallyFocusOnConfirm: false,
  initiallyFocusOnCancel: false,
  modal: true,
});

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'confirm', value: MouseEvent): void;
  (e: 'close'): void;
}>();

const attrs = useAttrs();
const slots = defineSlots<{
  default(): VNode;
  trigger(): VNode;
  content(): VNode;
  description(): VNode;
  buttons(): VNode;
}>();
const confirmButton = ref<InstanceType<typeof KaButton> | null>();
const cancelButton = ref<InstanceType<typeof KaButton> | null>();

const isConfirm = computed(() => props.preset === 'confirm');
const hasDescription = computed(() => !!props.description || !!slots.description);

if (import.meta.env.DEV) {
  watchEffect(() => {
    if (!props.description && !slots.description) {
      // eslint-disable-next-line no-console
      console.warn(
        '[KaModal] Provide either a `description` prop or a `#description` slot for accessibility (aria-describedby).',
      );
    }
    if (isConfirm.value && !props.title) {
      // eslint-disable-next-line no-console
      console.warn(
        '[KaModal] confirm preset requires a `title` prop for accessibility (aria-labelledby).',
      );
    }
  });
}

const { dataTestid, suffix } = useDataTestid(
  (attrs.id as string) || (isConfirm.value ? 'confirmation-dialog' : `modal`),
);

const internalWidth = computed(() => props.width || (isConfirm.value ? 'full' : 'auto'));

const { handleCloseAutoFocus } = useDialogTriggerFocus(
  toRef(props, 'open'),
  toRef(props, 'triggerEl'),
);

// Handle custom focus when dialog opens
function handleOpenAutoFocus(event: Event) {
  const focusActions = [
    { condition: props.initiallyFocusOnConfirm, element: confirmButton.value?.$el },
    { condition: props.initiallyFocusOnCancel, element: cancelButton.value?.$el },
    { condition: !!props.initialFocus, element: props.initialFocus },
  ];

  const actionToExecute = focusActions.find((action) => action.condition && action.element);
  if (actionToExecute?.element && typeof actionToExecute.element.focus === 'function') {
    event.preventDefault();
    nextTick(() => actionToExecute.element.focus());
  }
}

function closeDialog() {
  emit('update:open', false);
  emit('close');
}

const internalOpen = computed({
  get: () => props.open,
  set: (value: boolean) => {
    emit('update:open', value);
    if (!value) {
      emit('close');
    }
  },
});
</script>
<template>
  <DialogRoot v-model:open="internalOpen" :modal="modal">
    <DialogPortal>
      <Transition v-bind="modalOverlayTransition">
        <DialogOverlay
          v-if="props.open"
          :class="cnMerge([modalOverlayClasses, props.zIndexClass, props.overlayClasses])"
        />
      </Transition>

      <div
        :class="
          cnMerge([
            modalContainerClasses,
            props.zIndexClass,
            // Disable pointer events when modal is closed
            // This is needed so that the modal container stays in the DOM
            // in order to preserve animation transitions, but not block interactions
            { 'pointer-events-none': !props.open },
          ])
        "
      >
        <Transition v-bind="modalContentTransition">
          <DialogContent
            v-if="props.open"
            :aria-modal="modal ? 'true' : undefined"
            v-bind="attrs['aria-labelledby'] ? { 'aria-labelledby': attrs['aria-labelledby'] } : {}"
            :data-testid="dataTestid"
            :class="[
              modalContentClasses,
              { 'sm:max-w-lg': isConfirm, 'sm:w-full': internalWidth === 'full' },
              props.modalClasses,
            ]"
            @open-auto-focus="handleOpenAutoFocus"
            @close-auto-focus="handleCloseAutoFocus"
          >
            <DialogDescription v-if="hasDescription && !isConfirm" class="sr-only">
              <slot name="description">{{ props.description }}</slot>
            </DialogDescription>

            <div v-if="isConfirm" :class="modalConfirmBodyClasses">
              <DialogTitle :class="modalTitleClasses">
                {{ props.title }}
              </DialogTitle>

              <DialogDescription v-if="hasDescription" :class="modalDescriptionClasses">
                <slot name="description">{{ props.description }}</slot>
              </DialogDescription>

              <slot name="content" />

              <div class="mt-5">
                <slot name="buttons">
                  <div :class="modalButtonsGridClasses">
                    <KaButton
                      ref="confirmButton"
                      variant="primary"
                      :data-testid="suffix('confirm')"
                      :loading="isPending"
                      :use-focus-visible="false"
                      :type="submitBtnFormAttr ? 'submit' : 'button'"
                      :form="submitBtnFormAttr"
                      class="sm:col-start-2"
                      @click="emit('confirm', $event)"
                    >
                      {{ props.confirmText }}
                    </KaButton>
                    <DialogClose as-child>
                      <KaButton
                        ref="cancelButton"
                        :data-testid="suffix('cancel')"
                        :use-focus-visible="false"
                        class="sm:col-start-1"
                        @click="closeDialog"
                      >
                        {{ props.dismissText }}
                      </KaButton>
                    </DialogClose>
                  </div>
                </slot>
              </div>
            </div>
            <template v-else>
              <DialogTitle v-if="props.title" class="sr-only">{{ props.title }}</DialogTitle>
              <slot name="default" />
            </template>
          </DialogContent>
        </Transition>
      </div>
    </DialogPortal>
  </DialogRoot>
</template>
